'use client';

import React, { useEffect, useState, useRef } from 'react'
import { Button } from "@/components/ui/button"
import { ChevronDown } from "lucide-react"
import { motion } from "framer-motion"

const VIDEO_DURATION = 7000; // 7 seconds in milliseconds

const LandingPage: React.FC = () => {
  const [showSubtitle, setShowSubtitle] = useState(false)
  const [videos, setVideos] = useState<string[]>([])
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Start subtitle animation after tagline animation completes
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSubtitle(true)
    }, 3000)
    return () => clearTimeout(timer)
  }, [])

  // Fetch video files
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch('/api/videos')
        const videoList = await response.json()
        console.log('Fetched videos:', videoList)
        if (Array.isArray(videoList) && videoList.length > 0) {
          setVideos(videoList)
        } else {
          console.error('Invalid video list received:', videoList)
        }
      } catch (error) {
        console.error('Error fetching videos:', error)
      }
    }
    fetchVideos()
  }, [])

  // Handle video switching
  useEffect(() => {
    if (videos.length === 0) {
      console.log('No videos available')
      return
    }

    console.log('Setting up video rotation with', videos.length, 'videos')
    
    // Function to switch videos
    const switchVideo = () => {
      setCurrentVideoIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % videos.length
        console.log(`Switching from video ${prevIndex} to ${nextIndex}`)
        return nextIndex
      })
    }

    // Set up the interval for switching
    const interval = setInterval(switchVideo, VIDEO_DURATION)

    // Clean up
    return () => {
      console.log('Cleaning up video interval')
      clearInterval(interval)
    }
  }, [videos]) // Only re-run when videos array changes

  // Handle video element updates
  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement || videos.length === 0) return

    console.log('Current video index:', currentVideoIndex, 'Video:', videos[currentVideoIndex])
    
    let playAttempt: number | null = null;
    
    const attemptPlay = () => {
      if (playAttempt) {
        clearTimeout(playAttempt);
      }
      
      videoElement.currentTime = 0;
      videoElement.play()
        .then(() => {
          console.log('Started playing video');
          playAttempt = null;
        })
        .catch(err => {
          if (err.name === 'AbortError') {
            // If playback was aborted, try again after a short delay
            console.log('Playback interrupted, retrying...');
            playAttempt = window.setTimeout(attemptPlay, 1000);
          } else {
            console.error('Error playing video:', err);
          }
        });
    };

    attemptPlay();

    // Add event listeners
    const onEnded = () => {
      console.log('Video ended naturally');
    }

    const onError = (e: Event) => {
      console.error('Video error:', e);
      // Try to recover from error by attempting playback again
      attemptPlay();
    }

    videoElement.addEventListener('ended', onEnded);
    videoElement.addEventListener('error', onError);

    return () => {
      if (playAttempt) {
        clearTimeout(playAttempt);
      }
      videoElement.removeEventListener('ended', onEnded);
      videoElement.removeEventListener('error', onError);
    }
  }, [currentVideoIndex, videos])

  // Reset scroll position on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative">
      {/* Hero section with background video */}
      <section className="relative h-screen overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full bg-black">
          {videos.length > 0 && (
            <video
              key={videos[currentVideoIndex]}
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            >
              <source src={`/videos/landingpage/${videos[currentVideoIndex]}`} type="video/mp4" />
            </video>
          )}
        </div>
        
        {/* Overlay to improve text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#9b59b6]/70 to-transparent"></div>
        
        {/* Content overlay */}
        <div className="container mx-auto relative z-10 h-full flex items-center pt-0 pb-16">
          <div className="max-w-2xl text-white px-4 mt-[-80px]">
            <motion.h1 
              className="text-5xl lg:text-5xl font-bold mb-8 leading-[1.15] drop-shadow-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1 }}
            >
              {/* Animate each letter */}
              {"AN ECONOMICALLY INTEGRATED WORLD WITH INFINITE POSSIBILITIES...".split("").map((char, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.1,
                    delay: index * 0.05,
                    ease: "easeOut"
                  }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>

            <motion.p 
              className="text-xl lg:text-xl leading-relaxed drop-shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: showSubtitle ? 1 : 0, y: showSubtitle ? 0 : 20 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              AI and Blockchain enabled cutting edge Global Marketplace Platform, integrating Intelligence-as-a-service, across all markets, industry sectors, businesses and people....the world's most potent business network to connect, engage and transact seamlessly!
            </motion.p>
          </div>
        </div>

        {/* Scroll down indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:text-[#9b59b6] animate-bounce"
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <ChevronDown className="h-8 w-8" />
          </Button>
        </div>
      </section>
    </div>
  )
}

export default LandingPage
